/**
 * V55.64: 인사관리 > 인사평가 현황 페이지
 *
 * 2탭 구성:
 * - 1탭: 평가서 열람 (미구현 placeholder)
 * - 2탭: 면담기록 열람 (HR에게 전송된 면담 기록)
 *
 * @module pages/hr/HrPerformancePage
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ModalOverlay } from '../../shared/components/ModalOverlay';
import { useNavigate } from 'react-router-dom';
import { FileText, MessageSquare, Loader, AlertTriangle, ChevronRight, RefreshCw } from 'lucide-react';
import { Pagination, paginateData } from '../../shared/components/Pagination';
import { useHrInterviewShares, HrInterviewShareListItem } from '../../shared/hooks/useHrInterviewShares';
import {
  useHrEvaluations,
  EVALUATION_TYPE_LABELS,
  EVALUATION_STATUS_LABELS,
  FINAL_GRADE_LABELS,
  EvaluationItem,
  EvaluationStatus,
  EvaluationTotals,
} from '../../shared/hooks/useEvaluations';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import { ProbationEvaluationForm, isProbationType } from '../../shared/components/ProbationEvaluationForm';
import { WorkEvaluationContent } from '../../shared/components/WorkEvaluationContent';

/** V72: 최종결과 칼럼 표시 — 수습평가는 점수, 근무평가는 최종결정 */
function getFinalResultDisplay(evalItem: EvaluationItem): string {
  if (isProbationType(evalItem.templateType)) {
    // 수습평가: 점수값 (예: "A+ (4.3)")
    return evalItem.finalGradeDetail
      || (evalItem.finalGrade ? FINAL_GRADE_LABELS[evalItem.finalGrade] : '-');
  }

  // 근무평가: 해당부서 결정 (연장, 전환 등)
  const mgr = evalItem.managerEvaluation as Record<string, any> | null | undefined;
  if (mgr) {
    const decision = mgr['sys_d_t03'] || mgr['sys_c_t03'];
    if (decision) return String(decision);
  }

  // fallback
  return evalItem.finalGrade ? FINAL_GRADE_LABELS[evalItem.finalGrade] : '-';
}

/** V267: WorkEvalReadOnlyView 제거 — WorkEvaluationContent 공통 컴포넌트로 대체 */

// ============================================
// 상수
// ============================================

type TabId = 'evaluations' | 'interviews';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'evaluations', label: '평가서 열람', icon: <FileText size={16} /> },
  { id: 'interviews', label: '면담기록 열람', icon: <MessageSquare size={16} /> },
];

// ============================================
// 메인 컴포넌트
// ============================================

export function HrPerformancePage() {
  const navigate = useNavigate();
  const { hasPermission, hasAnyRole } = useAuthorization();

  // V60: 평가서 열람 탭 - HR/어드민 + role_exec(읽기 전용)
  const canViewEvaluations = hasPermission('evaluation.manage')
    || hasAnyRole(['role_hr', 'system_admin', 'role_admin_kr', 'role_admin_cn', 'role_exec']);
  const visibleTabs = useMemo(
    () => (canViewEvaluations ? TABS : TABS.filter(t => t.id !== 'evaluations')),
    [canViewEvaluations],
  );

  const [activeTab, setActiveTab] = useState<TabId>('evaluations'); // 기본: 평가서 열람 탭

  return (
    <div style={{ padding: '24px' }}>
      {/* 페이지 헤더 */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-gray-800)' }}>
          인사평가 현황
        </h1>
        <p style={{ marginTop: '4px', color: 'var(--color-gray-500)', fontSize: '14px' }}>
          직원 평가서 및 면담기록을 열람합니다.
        </p>
      </div>

      {/* 탭 네비게이션 */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          borderBottom: '1px solid var(--color-gray-200)',
          marginBottom: '24px',
        }}
      >
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? 'var(--color-blue-600)' : 'var(--color-gray-600)',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid var(--color-blue-600)' : '2px solid transparent',
              cursor: 'pointer',
              marginBottom: '-1px',
              transition: 'color 0.2s, border-color 0.2s',
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* 탭 컨텐츠 */}
      {activeTab === 'evaluations' && canViewEvaluations && <EvaluationsTab />}
      {activeTab === 'interviews' && <InterviewsTab onViewDetail={(shareId) => navigate(`/hr/performance/interviews/${shareId}`)} />}
    </div>
  );
}

// ============================================
// 1탭: 평가서 열람
// ============================================

function HrV2TotalsDisplay({ totals, label }: { totals: EvaluationTotals; label: string }) {
  const categories = Object.values(totals.byCategory);
  return (
    <div style={{ background: 'var(--color-gray-50)', padding: '12px', borderRadius: '6px', marginBottom: '12px' }}>
      <strong style={{ fontSize: '13px' }}>{label}</strong>
      {categories.map(cat => (
        <div key={cat.nodeId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginTop: '4px' }}>
          <span>{cat.label}</span><span>{Number(cat.total).toFixed(2)} / {Number(cat.maxPossible).toFixed(2)}</span>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginTop: '6px', paddingTop: '4px', borderTop: '1px solid var(--color-gray-200)' }}>
        <span>합계</span><span>{Number(totals.grandTotal).toFixed(2)} / {Number(totals.grandMaxPossible).toFixed(2)}</span>
      </div>
    </div>
  );
}

function EvaluationsTab() {
  // V61: role_exec 조회 전용 — 승인/반려 버튼 숨김
  // V62: auth_roles DB에 evaluation.manage 미반영 시 역할 기반 폴백 추가
  const { hasPermission: hasEvalPerm, hasAnyRole: hasEvalRole } = useAuthorization();
  const canManageEvaluations = hasEvalPerm('evaluation.manage')
    || hasEvalRole(['role_hr', 'system_admin', 'role_admin_kr', 'role_admin_cn']);

  const {
    evaluations,
    loading,
    error,
    listHrEvaluations,
    getEvaluationDetail,
    finalizeProbationEvaluation,
    finalizeWorkEvaluation,
  } = useHrEvaluations();

  // V280: 수습평가 목록 중복 제거 — 동일 (targetUserId, evaluationCycle) 그룹당 최고 단계 1행만 표시
  // 원본 evaluations는 handleRowClick의 sibling 탐색에 그대로 사용
  const displayEvaluations = useMemo(() => {
    const typeOrder = ['probation_1m', 'probation_2m', 'probation_3m'];
    // 1차: 그룹별 최고 단계 찾기
    const highestByGroup = new Map<string, EvaluationItem>();
    for (const item of evaluations) {
      if (!isProbationType(item.templateType)) continue;
      const key = `${item.targetUserId}_${item.evaluationCycle || 1}`;
      const existing = highestByGroup.get(key);
      if (!existing || typeOrder.indexOf(item.templateType) > typeOrder.indexOf(existing.templateType)) {
        highestByGroup.set(key, item);
      }
    }
    // 2차: 비수습은 전부 유지, 수습은 그룹당 최고 단계만 유지 (원래 순서 보존)
    const seen = new Set<string>();
    return evaluations.filter(item => {
      if (!isProbationType(item.templateType)) return true;
      const key = `${item.targetUserId}_${item.evaluationCycle || 1}`;
      if (seen.has(key)) return false;
      const highest = highestByGroup.get(key);
      if (item.id !== highest?.id) return false;
      seen.add(key);
      return true;
    });
  }, [evaluations]);

  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState<EvaluationStatus | ''>('sent_to_hr');
  // V64: 수습 평가 1~3차 일괄 표시 지원
  const [selectedEvals, setSelectedEvals] = useState<EvaluationItem[]>([]);
  const primaryEval = selectedEvals.length > 0
    ? (selectedEvals.find(e => e.templateType === 'probation_3m') || selectedEvals[selectedEvals.length - 1])
    : null;
  // V59: HR 결재
  const [hrComment, setHrComment] = useState('');
  const [finalizing, setFinalizing] = useState(false);

  // V66 (재수정): 수습평가 PDF 다운로드 — 모달 본문을 그대로 인쇄.
  //   기존 BE 신규 PDF 템플릿(generateProbationEvaluationPdf)은 화면과 불일치/잘림 이슈로 폐기.
  //   FE 가 ProbationEvaluationForm + 헤더/코멘트 들을 readonly 로 이미 렌더하고 있으므로
  //   해당 영역을 .probation-print-area 로 표시 → window.print() 호출 → 사용자가 "PDF로 저장".
  //   각 차수 div 에 .probation-print-page (page-break-after) 부여, footer/배경 .probation-print-hide.
  const handleDownloadProbationPdf = (_evalItem: EvaluationItem) => {
    // V66 (1page fit): 인쇄 다이얼로그 전 사용자 안내 — 브라우저 머리글/바닥글 체크 해제.
    alert(
      'PDF 저장 시 다음 옵션을 권장합니다:\n' +
      '· 대상: "PDF로 저장"\n' +
      '· "더 보기" → "머리글 및 바닥글" 체크 해제\n' +
      '· 배율: "기본" (자동 맞춤 비활성화 권장)'
    );
    window.print();
  };
  const pdfDownloading = false; // 인쇄 다이얼로그는 즉시 동기 호출 — 별도 상태 불필요

  // V71: 페이지네이션
  const [evalPage, setEvalPage] = useState(1);
  const EVAL_PAGE_SIZE = 15;

  const handleFinalize = async (decision: 'approved' | 'rejected') => {
    if (!primaryEval) return;
    const label = decision === 'approved' ? '승인' : '반려';
    const evalLabel = isProbationType(primaryEval.templateType) ? '수습 평가' : '근무평가';
    if (!window.confirm(`${primaryEval.targetUserName}의 ${evalLabel}를 ${label}하시겠습니까?`)) return;
    setFinalizing(true);
    try {
      // V72: 수습평가 vs 근무평가 분기
      const finalizeFn = isProbationType(primaryEval.templateType)
        ? finalizeProbationEvaluation
        : finalizeWorkEvaluation;
      const success = await finalizeFn({
        evaluationId: primaryEval.id,
        decision,
        hrComment: hrComment || undefined,
      });
      if (success) {
        alert(`${label} 처리되었습니다.`);
        setSelectedEvals([]);
        setHrComment('');
        listHrEvaluations({ year: yearFilter, status: statusFilter || undefined });
      }
    } finally {
      setFinalizing(false);
    }
  };

  useEffect(() => {
    setEvalPage(1);
    listHrEvaluations({
      year: yearFilter,
      status: statusFilter || undefined,
    });
  }, [yearFilter, statusFilter, listHrEvaluations]);

  const handleRowClick = async (evalItem: EvaluationItem) => {
    if (isProbationType(evalItem.templateType)) {
      // V64: 수습 평가 1~3차 일괄 로드
      const cycle = evalItem.evaluationCycle || 1;
      const siblings = evaluations.filter(e =>
        e.targetUserId === evalItem.targetUserId
        && isProbationType(e.templateType)
        && (e.evaluationCycle || 1) === cycle
      );
      const typeOrder = ['probation_1m', 'probation_2m', 'probation_3m'];
      siblings.sort((a, b) => typeOrder.indexOf(a.templateType) - typeOrder.indexOf(b.templateType));

      const details: EvaluationItem[] = [];
      for (const s of siblings) {
        const detail = await getEvaluationDetail(s.id);
        if (detail) details.push(detail);
      }
      if (details.length > 0) setSelectedEvals(details);
    } else {
      const detail = await getEvaluationDetail(evalItem.id);
      if (detail) setSelectedEvals([detail]);
    }
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div>
      {/* 필터 */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
        <select
          value={yearFilter}
          onChange={e => setYearFilter(Number(e.target.value))}
          style={{ padding: '6px 12px', border: '1px solid var(--color-gray-200)', borderRadius: '4px', fontSize: '14px' }}
        >
          {yearOptions.map(y => (
            <option key={y} value={y}>{y}년</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as EvaluationStatus | '')}
          style={{ padding: '6px 12px', border: '1px solid var(--color-gray-200)', borderRadius: '4px', fontSize: '14px' }}
        >
          <option value="sent_to_hr">{EVALUATION_STATUS_LABELS.sent_to_hr}</option>
          <option value="hr_approved">{EVALUATION_STATUS_LABELS.hr_approved}</option>
          <option value="hr_rejected">{EVALUATION_STATUS_LABELS.hr_rejected}</option>
          <option value="">전체</option>
        </select>
        <button
          onClick={() => listHrEvaluations({ year: yearFilter, status: statusFilter || undefined })}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '6px 12px', fontSize: '13px',
            background: 'var(--color-gray-100)', color: 'var(--color-gray-700)',
            border: 'none', borderRadius: '6px', cursor: 'pointer',
          }}
        >
          <RefreshCw size={14} />
          새로고침
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px', background: 'var(--color-red-50)', color: 'var(--color-red-600)', borderRadius: '4px', marginBottom: '16px', fontSize: '14px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px', color: 'var(--color-gray-500)' }}>
          <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ marginLeft: '8px' }}>로딩 중...</span>
        </div>
      ) : displayEvaluations.length === 0 ? (
        <div style={{ padding: '60px 40px', background: 'var(--color-gray-50)', borderRadius: '8px', textAlign: 'center' }}>
          <FileText size={48} style={{ color: 'var(--color-gray-400)', marginBottom: '16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--color-gray-700)', marginBottom: '8px' }}>
            수신된 평가서가 없습니다
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--color-gray-500)' }}>
            조직장이 최종평가를 HR에 발송하면 여기에 표시됩니다.
          </p>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: '8px', border: '1px solid var(--color-gray-200)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-gray-50)' }}>
                <th style={thStyle}>대상자</th>
                <th style={thStyle}>소속</th>
                <th style={thStyle}>평가 유형</th>
                <th style={thStyle}>상태</th>
                <th style={thStyle}>최종결과</th>
                <th style={thStyle}>평가기간</th>
                <th style={{ ...thStyle, width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {paginateData(displayEvaluations, evalPage, EVAL_PAGE_SIZE).map(evalItem => (
                <tr
                  key={evalItem.id}
                  onClick={() => handleRowClick(evalItem)}
                  style={{ cursor: 'pointer', borderBottom: '1px solid var(--color-gray-100)', transition: 'background 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-gray-50)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={tdStyle}><span style={{ fontWeight: 500 }}>{evalItem.targetUserName}</span></td>
                  <td style={tdStyle}>{evalItem.targetUserOrgUnitName}</td>
                  <td style={tdStyle}>{EVALUATION_TYPE_LABELS[evalItem.templateType]}</td>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500,
                      background: evalItem.status === 'hr_approved' ? '#D1FAE5'
                        : evalItem.status === 'hr_rejected' ? '#FEE2E2'
                        : evalItem.status === 'sent_to_hr' ? 'var(--color-green-50)' : 'var(--color-blue-50)',
                      color: evalItem.status === 'hr_approved' ? '#065F46'
                        : evalItem.status === 'hr_rejected' ? '#991B1B'
                        : evalItem.status === 'sent_to_hr' ? 'var(--color-green-600)' : 'var(--color-blue-600)',
                    }}>
                      {EVALUATION_STATUS_LABELS[evalItem.status]}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>
                    {getFinalResultDisplay(evalItem)}
                  </td>
                  <td style={tdStyle}>{evalItem.evaluationPeriod || evalItem.evaluationYear}</td>
                  <td style={tdStyle}><ChevronRight size={16} style={{ color: 'var(--color-gray-400)' }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* V71: 페이지네이션 */}
      {!loading && displayEvaluations.length > 0 && (
        <Pagination
          currentPage={evalPage}
          totalItems={displayEvaluations.length}
          pageSize={EVAL_PAGE_SIZE}
          onPageChange={setEvalPage}
        />
      )}

      {/* V64: 상세 모달 — 수습 평가 1~3차 일괄 표시 */}
      {primaryEval && (
        <ModalOverlay
          className="probation-print-overlay"
          onClose={() => { setSelectedEvals([]); setHrComment(''); }}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          {/* V66 (1page fit): 각 차수가 A4 1페이지에 들어가도록 margin 최소화 + scale 축소 + 간격 압축 */}
          <style>{`
            @media print {
              @page { size: A4; margin: 6mm 6mm; }
              body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              body * { visibility: hidden; }
              .probation-print-area, .probation-print-area * { visibility: visible; }
              .probation-print-area {
                position: absolute !important; left: 0 !important; top: 0 !important;
                width: 100% !important; max-width: none !important; max-height: none !important;
                min-width: 0 !important;
                background: white !important; box-shadow: none !important; border-radius: 0 !important;
                padding: 0 !important; margin: 0 !important; overflow: visible !important;
                font-size: 11px !important; line-height: 1.35 !important;
              }
              .probation-print-area button { display: none !important; }
              .probation-print-overlay {
                position: static !important; background: none !important;
                display: block !important; height: auto !important;
              }
              .probation-print-hide { display: none !important; }

              /* V66: 차수별 1페이지 강제 — scale 0.85 로 축소, top-left 기준, 너비 보정 */
              .probation-print-page {
                page-break-after: always;
                page-break-inside: avoid;
                transform: scale(0.85);
                transform-origin: top left;
                width: 117.6% !important; /* 1/0.85 ≈ 117.6% — scale 후 100% 폭 유지 */
                margin-bottom: -10mm !important; /* scale 후 남는 여백 보정 */
              }
              .probation-print-page:last-of-type { page-break-after: auto; }

              /* V66 (3차 분리): 3차 wrapper 자체는 scale 무력화 — 안의 part-1/part-2 각각 scale + page-break */
              .probation-print-page.probation-third {
                transform: none !important;
                width: 100% !important;
                margin-bottom: 0 !important;
                page-break-inside: auto !important;
              }
              .probation-third-part-1 {
                transform: scale(0.85);
                transform-origin: top left;
                width: 117.6% !important;
                margin-bottom: -10mm !important;
                page-break-after: always;
                page-break-inside: avoid;
              }
              .probation-third-part-2 {
                transform: scale(0.85);
                transform-origin: top left;
                width: 117.6% !important;
                margin-bottom: -10mm !important;
                page-break-before: always;
                break-before: page;
                page-break-inside: avoid;
              }

              /* 페이지 내부 분리 방지 — 표/문단/섹션 */
              .probation-print-area table,
              .probation-print-area pre,
              .probation-print-area p,
              .probation-print-area h3,
              .probation-print-area h4 { page-break-inside: avoid; }

              /* 간격 압축 */
              .probation-print-area h3 { font-size: 14px !important; margin: 4px 0 6px !important; }
              .probation-print-area h4 { font-size: 12px !important; margin: 6px 0 4px !important; }
              .probation-print-area table { font-size: 10.5px !important; }
              .probation-print-area table th,
              .probation-print-area table td { padding: 3px 6px !important; }
              .probation-print-area p { margin: 2px 0 !important; }
              .probation-print-area > div > div[style*="grid-template-columns"] { gap: 6px !important; margin-bottom: 8px !important; }
              .probation-print-area .probation-print-page { padding-top: 4px !important; margin-top: 6px !important; border-top-width: 1px !important; }
              .probation-print-area .probation-print-page:first-child { padding-top: 0 !important; margin-top: 0 !important; border-top: none !important; }
            }
          `}</style>
          <div
            className="probation-print-area"
            style={{
              background: 'white', borderRadius: '8px', padding: '24px', width: '95%', maxWidth: '1100px', maxHeight: '90vh',
              overflow: 'auto',
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              {selectedEvals.length > 1 ? `${primaryEval.targetUserName} 수습평가` : primaryEval.templateTitle?.replace(/\s*\(시스템\)/, '')}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px', fontSize: '14px' }}>
              <div><span style={{ color: 'var(--color-gray-500)' }}>대상자:</span> {primaryEval.targetUserName}</div>
              <div><span style={{ color: 'var(--color-gray-500)' }}>소속:</span> {primaryEval.targetUserOrgUnitName}</div>
              {/* V66: 멘토 → 평가자 순서. mentorName 없으면 미표기. */}
              {primaryEval.leadEvaluatorName && (
                <div><span style={{ color: 'var(--color-gray-500)' }}>멘토:</span> {primaryEval.leadEvaluatorName}</div>
              )}
              <div><span style={{ color: 'var(--color-gray-500)' }}>평가자:</span> {primaryEval.evaluatorName}</div>
              {selectedEvals.length <= 1 && (
                <div><span style={{ color: 'var(--color-gray-500)' }}>유형:</span> {EVALUATION_TYPE_LABELS[primaryEval.templateType]}</div>
              )}
              {(primaryEval.finalGrade || primaryEval.managerEvaluation) && (
                <div><span style={{ color: 'var(--color-gray-500)' }}>최종결과:</span> <strong>{getFinalResultDisplay(primaryEval)}</strong></div>
              )}
              {primaryEval.evaluationPeriod && (
                <div><span style={{ color: 'var(--color-gray-500)' }}>기간:</span> {primaryEval.evaluationPeriod}</div>
              )}
            </div>

            {/* V64: 각 평가 라운드 순서대로 표시 */}
            {selectedEvals.map((evalItem, index) => {
              // V66 (3차 분리, 재수정): 3차 평가는 "멘토까지 1page + 팀장총평+HR 1page" 두 페이지 사용.
              //   - part-1 ProbationEvaluationForm 에 hideManagerSection={true} 로 _n4 미출력
              //   - part-2 에 _n4 inline 표 + managerComment + finalComment + HR 의견 모두 통합
              //   - 외부 영역은 !has3rd 분기 (3차 보유 시 외부 영역 미출력)
              const is3rd = evalItem.templateType === 'probation_3m';
              const formAndComments = (
                <>
                  {selectedEvals.length > 1 && (
                    <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: 'var(--color-gray-800)' }}>
                      {EVALUATION_TYPE_LABELS[evalItem.templateType]}
                    </h4>
                  )}

                  {isProbationType(evalItem.templateType) && (evalItem.templateNodes?.length || 0) > 0 ? (
                    <ProbationEvaluationForm
                      mode="readonly"
                      nodes={evalItem.templateNodes || []}
                      items={evalItem.templateItems || []}
                      scoringScale={evalItem.templateScoringScale || { min: 1, max: 4, step: 1 }}
                      templateType={evalItem.templateType}
                      selfEvaluation={evalItem.selfEvaluation || undefined}
                      leadEvaluation={evalItem.leadEvaluation || undefined}
                      managerEvaluation={evalItem.managerEvaluation || undefined}
                      formData={{}}
                      selfTotals={evalItem.selfTotals}
                      managerTotals={evalItem.managerTotals}
                      attendanceSummary={evalItem.attendanceSummary}
                      // V66 (재수정): 3차의 part-1 에서는 form 내부 _n4(팀장 총평) 미출력 — part-2 에 별도 렌더
                      hideManagerSection={is3rd}
                    />
                  ) : (evalItem.templateNodes?.length || 0) > 0 ? (
                    /* V267: 근무평가 — 조직장 평가서 UI와 동일한 공통 컴포넌트 (readOnly) */
                    <WorkEvaluationContent
                      evaluation={evalItem}
                      formData={evalItem.managerEvaluation as Record<string, any> || {}}
                    />
                  ) : (
                    <>
                      {evalItem.selfTotals && <HrV2TotalsDisplay totals={evalItem.selfTotals} label="자가평가 점수" />}
                      {evalItem.leadTotals && <HrV2TotalsDisplay totals={evalItem.leadTotals} label="멘토평가 점수" />}
                      {evalItem.managerTotals && <HrV2TotalsDisplay totals={evalItem.managerTotals} label="팀장평가 점수" />}
                    </>
                  )}

                  {evalItem.selfComment && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>자가평가 총평:</strong>
                      <p style={{ background: 'var(--color-gray-50)', padding: '12px', borderRadius: '4px', margin: '4px 0', fontSize: '14px' }}>{evalItem.selfComment}</p>
                    </div>
                  )}
                  {/* V66: 3차 평가(probation_3m)에서는 "멘토 총평" 미노출.
                      "멘토 종합의견"(폼 내 _i11)은 ProbationEvaluationForm 안에서 그대로 렌더되므로 영향 없음. */}
                  {evalItem.leadComment && evalItem.templateType !== 'probation_3m' && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>멘토 총평:</strong>
                      <p style={{ background: 'var(--color-gray-50)', padding: '12px', borderRadius: '4px', margin: '4px 0', fontSize: '14px' }}>{evalItem.leadComment}</p>
                    </div>
                  )}
                </>
              );

              const managerCommentNode = evalItem.managerComment ? (
                <div style={{ marginBottom: '12px' }}>
                  <strong>팀장 총평:</strong>
                  <p style={{ background: 'var(--color-gray-50)', padding: '12px', borderRadius: '4px', margin: '4px 0', fontSize: '14px' }}>{evalItem.managerComment}</p>
                </div>
              ) : null;

              // V66 (재수정): 3차 part-2 — _n4 inline 표 + managerComment + finalComment + HR 의견 통합
              let managerNodeRender: React.ReactNode = null;
              if (is3rd) {
                const managerNode = (evalItem.templateNodes || []).find((n: any) => n.title === '팀장 총평');
                const managerNodeItems = managerNode
                  ? (evalItem.templateItems || []).filter((it: any) => it.nodeId === managerNode.id)
                  : [];
                const hasManagerNodeData = managerNodeItems.some((it: any) => evalItem.managerEvaluation?.[it.id]);
                if (hasManagerNodeData) {
                  managerNodeRender = (
                    <div style={{ marginBottom: 16, padding: 12, background: '#F9FAFB', borderRadius: 6, border: '1px solid #E5E7EB' }}>
                      <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#1F2937' }}>팀장 총평</h4>
                      {managerNodeItems.map((it: any) => (
                        <div key={it.id} style={{ marginBottom: 6, fontSize: 13 }}>
                          <strong style={{ color: '#374151' }}>{it.title}: </strong>
                          <span>{evalItem.managerEvaluation?.[it.id] || '-'}</span>
                        </div>
                      ))}
                    </div>
                  );
                }
              }

              // V66 (재수정): 3차 part-2 안 finalComment + HR 의견 — has3rd 시 외부 영역 대신 part-2 안에 출력
              const finalCommentNode = is3rd && primaryEval.finalComment ? (
                <div style={{ marginBottom: '12px' }}>
                  <strong>최종 코멘트:</strong>
                  <p style={{ background: 'var(--color-gray-50)', padding: '12px', borderRadius: '4px', margin: '4px 0', fontSize: '14px' }}>{primaryEval.finalComment}</p>
                </div>
              ) : null;
              const hrCommentInput = is3rd && primaryEval.status === 'sent_to_hr' ? (
                <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--color-gray-50)', borderRadius: '6px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: 'var(--color-gray-700)' }}>HR 코멘트 (선택)</label>
                  <textarea
                    value={hrComment}
                    onChange={e => setHrComment(e.target.value)}
                    placeholder="승인/반려 사유를 입력하세요..."
                    style={{
                      width: '100%', padding: '8px', border: '1px solid var(--color-gray-200)',
                      borderRadius: '4px', fontSize: '13px', minHeight: '60px', resize: 'vertical',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              ) : null;
              const hrResultNode = is3rd && (primaryEval.status === 'hr_approved' || primaryEval.status === 'hr_rejected' || primaryEval.status === 'rejected') ? (
                <div style={{
                  marginBottom: '12px', padding: '12px', borderRadius: '6px',
                  background: primaryEval.status === 'hr_approved' ? '#D1FAE5' : '#FEE2E2',
                }}>
                  <strong style={{ fontSize: '13px', color: primaryEval.status === 'hr_approved' ? '#065F46' : '#991B1B' }}>
                    HR {primaryEval.status === 'hr_approved' ? '승인' : '반려'}
                  </strong>
                  {primaryEval.hrComment && <p style={{ margin: '4px 0 0', fontSize: '13px' }}>{primaryEval.hrComment}</p>}
                </div>
              ) : null;

              const part2HasContent = !!(managerNodeRender || managerCommentNode || finalCommentNode || hrCommentInput || hrResultNode);

              return (
                <div
                  key={evalItem.id}
                  className={`probation-print-page${is3rd ? ' probation-third' : ''}`}
                  style={index > 0 ? { borderTop: '2px solid var(--color-gray-200)', paddingTop: 16, marginTop: 16 } : undefined}
                >
                  {is3rd ? (
                    <>
                      <div className="probation-third-part-1">{formAndComments}</div>
                      {part2HasContent && (
                        <div className="probation-third-part-2">
                          {managerNodeRender}
                          {managerCommentNode}
                          {finalCommentNode}
                          {hrCommentInput}
                          {hrResultNode}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {formAndComments}
                      {managerCommentNode}
                    </>
                  )}
                </div>
              );
            })}

            {/* V66 (재수정): 외부 finalComment + HR 의견 — 3차 보유 시 part-2 안에서 출력하므로 여기선 미출력 */}
            {!selectedEvals.some((e) => e.templateType === 'probation_3m') && (
              <>
                {primaryEval.finalComment && (
                  <div style={{ marginBottom: '12px' }}>
                    <strong>최종 코멘트:</strong>
                    <p style={{ background: 'var(--color-gray-50)', padding: '12px', borderRadius: '4px', margin: '4px 0', fontSize: '14px' }}>{primaryEval.finalComment}</p>
                  </div>
                )}

                {/* V59+V72: HR 결재 — sent_to_hr 상태 (수습+근무 모두) */}
                {primaryEval.status === 'sent_to_hr' && (
                  <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--color-gray-50)', borderRadius: '6px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: 'var(--color-gray-700)' }}>
                      HR 코멘트 (선택)
                    </label>
                    <textarea
                      value={hrComment}
                      onChange={e => setHrComment(e.target.value)}
                      placeholder="승인/반려 사유를 입력하세요..."
                      style={{
                        width: '100%', padding: '8px', border: '1px solid var(--color-gray-200)',
                        borderRadius: '4px', fontSize: '13px', minHeight: '60px', resize: 'vertical',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                )}

                {/* V59+V72: HR 결재 결과 표시 (근무평가는 rejected 상태 사용) */}
                {(primaryEval.status === 'hr_approved' || primaryEval.status === 'hr_rejected' || primaryEval.status === 'rejected') && (
                  <div style={{
                    marginBottom: '12px', padding: '12px', borderRadius: '6px',
                    background: primaryEval.status === 'hr_approved' ? '#D1FAE5' : '#FEE2E2',
                  }}>
                    <strong style={{ fontSize: '13px', color: primaryEval.status === 'hr_approved' ? '#065F46' : '#991B1B' }}>
                      HR {primaryEval.status === 'hr_approved' ? '승인' : '반려'}
                    </strong>
                    {primaryEval.hrComment && (
                      <p style={{ margin: '4px 0 0', fontSize: '13px' }}>{primaryEval.hrComment}</p>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="probation-print-hide" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              {/* V66: 수습평가 1~3차 PDF 다운로드 (probation 타입에서만) — window.print() 호출 */}
              {isProbationType(primaryEval.templateType) && (
                <button
                  onClick={() => handleDownloadProbationPdf(primaryEval)}
                  disabled={pdfDownloading}
                  style={{
                    padding: '8px 16px', borderRadius: '4px', border: '1px solid var(--color-blue-600)',
                    background: pdfDownloading ? 'var(--color-gray-200)' : 'white',
                    color: pdfDownloading ? 'var(--color-gray-500)' : 'var(--color-blue-600)',
                    cursor: pdfDownloading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500,
                  }}
                >
                  {pdfDownloading ? '생성 중...' : '수습평가 PDF 다운로드'}
                </button>
              )}
              <button
                onClick={() => { setSelectedEvals([]); setHrComment(''); }}
                style={{ padding: '8px 16px', border: '1px solid var(--color-gray-200)', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
              >
                닫기
              </button>
              {primaryEval.status === 'sent_to_hr' && canManageEvaluations && (
                <>
                  <button
                    onClick={() => handleFinalize('rejected')}
                    disabled={finalizing}
                    style={{
                      padding: '8px 16px', borderRadius: '4px', border: 'none', fontSize: '14px', fontWeight: 500,
                      background: finalizing ? 'var(--color-gray-300)' : '#DC2626', color: 'white',
                      cursor: finalizing ? 'not-allowed' : 'pointer',
                    }}
                  >
                    반려
                  </button>
                  <button
                    onClick={() => handleFinalize('approved')}
                    disabled={finalizing}
                    style={{
                      padding: '8px 16px', borderRadius: '4px', border: 'none', fontSize: '14px', fontWeight: 500,
                      background: finalizing ? 'var(--color-gray-300)' : '#059669', color: 'white',
                      cursor: finalizing ? 'not-allowed' : 'pointer',
                    }}
                  >
                    승인
                  </button>
                </>
              )}
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}

// ============================================
// 2탭: 면담기록 열람
// ============================================

interface InterviewsTabProps {
  onViewDetail: (shareId: string) => void;
}

function InterviewsTab({ onViewDetail }: InterviewsTabProps) {
  const { shares, isLoading, error, refetch, hasMore, loadMore } = useHrInterviewShares();

  // V71: 페이지네이션
  const [interviewPage, setInterviewPage] = useState(1);
  const INTERVIEW_PAGE_SIZE = 15;

  // V71: 커서 기반 데이터 자동 로드 (V71.1: error 시 무한 루프 방지)
  useEffect(() => {
    if (hasMore && !isLoading && !error) {
      loadMore();
    }
  }, [hasMore, isLoading, error]);

  // 로딩 상태
  if (isLoading && shares.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px',
          color: 'var(--color-gray-500)',
        }}
      >
        <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '8px' }}>로딩 중...</span>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '60px',
          color: 'var(--color-red-600)',
          background: 'var(--color-red-50)',
          borderRadius: '8px',
        }}
      >
        <AlertTriangle size={32} style={{ marginBottom: '12px' }} />
        <p style={{ fontSize: '14px', marginBottom: '16px' }}>{error.message}</p>
        <button
          onClick={() => refetch()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            fontSize: '14px',
            background: 'var(--color-red-600)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          <RefreshCw size={14} />
          다시 시도
        </button>
      </div>
    );
  }

  // 빈 상태
  if (shares.length === 0) {
    return (
      <div
        style={{
          padding: '60px 40px',
          background: 'var(--color-gray-50)',
          borderRadius: '8px',
          textAlign: 'center',
        }}
      >
        <MessageSquare size={48} style={{ color: 'var(--color-gray-400)', marginBottom: '16px' }} />
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--color-gray-700)', marginBottom: '8px' }}>
          전송된 면담 기록이 없습니다
        </h3>
        <p style={{ fontSize: '14px', color: 'var(--color-gray-500)' }}>
          조직장이 상호합의된 면담을 HR에 전송하면 여기에 표시됩니다.
        </p>
      </div>
    );
  }

  // 테이블
  return (
    <div>
      {/* 새로고침 버튼 */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
        <button
          onClick={() => refetch()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            fontSize: '13px',
            background: 'var(--color-gray-100)',
            color: 'var(--color-gray-700)',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          <RefreshCw size={14} />
          새로고침
        </button>
      </div>

      {/* 테이블 */}
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          border: '1px solid var(--color-gray-200)',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--color-gray-50)' }}>
              <th style={thStyle}>면담대상자</th>
              <th style={thStyle}>조직</th>
              <th style={thStyle}>면담자(조직장)</th>
              <th style={thStyle}>면담일</th>
              <th style={thStyle}>면담시간</th>
              <th style={thStyle}>면담타입</th>
              <th style={thStyle}>상태</th>
              <th style={{ ...thStyle, width: '40px' }}></th>
            </tr>
          </thead>
          <tbody>
            {paginateData(shares, interviewPage, INTERVIEW_PAGE_SIZE).map((share) => (
              <InterviewShareRow key={share.id} share={share} onClick={() => onViewDetail(share.id)} />
            ))}
          </tbody>
        </table>
      </div>

      {/* V71: 페이지네이션 */}
      {shares.length > 0 && (
        <Pagination
          currentPage={interviewPage}
          totalItems={shares.length}
          pageSize={INTERVIEW_PAGE_SIZE}
          onPageChange={setInterviewPage}
        />
      )}
    </div>
  );
}

// ============================================
// 테이블 행 컴포넌트
// ============================================

interface InterviewShareRowProps {
  share: HrInterviewShareListItem;
  onClick: () => void;
}

function InterviewShareRow({ share, onClick }: InterviewShareRowProps) {
  return (
    <tr
      onClick={onClick}
      style={{
        cursor: 'pointer',
        borderBottom: '1px solid var(--color-gray-100)',
        transition: 'background 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--color-gray-50)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <td style={tdStyle}>
        <span style={{ fontWeight: 500 }}>{share.targetUserName}</span>
      </td>
      <td style={tdStyle}>{share.targetOrgUnitName}</td>
      <td style={tdStyle}>{share.interviewerName}</td>
      <td style={tdStyle}>{share.interviewDate}</td>
      <td style={tdStyle}>{share.interviewTime}</td>
      <td style={tdStyle}>{share.interviewTypeLabel}</td>
      <td style={tdStyle}>
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500,
            background: 'var(--color-green-50)',
            color: 'var(--color-green-600)',
          }}
        >
          {share.deliveryStatusLabel}
        </span>
      </td>
      <td style={tdStyle}>
        <ChevronRight size={16} style={{ color: 'var(--color-gray-400)' }} />
      </td>
    </tr>
  );
}

// ============================================
// 스타일
// ============================================

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--color-gray-600)',
  borderBottom: '1px solid var(--color-gray-200)',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: '14px',
  color: 'var(--color-gray-700)',
};
